import React, { useEffect, useRef, useState } from 'react';
import { restaurantInfo, translations } from '../data/mock';
import { Phone, Mail, MapPin, Facebook } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useLanguage } from '../context/LanguageContext';

const ContactSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    guests: '2',
    date: ''
  });
  const sectionRef = useRef(null);
  const { language } = useLanguage();
  const t = translations[language];

  // WhatsApp number formatting
  const whatsappNumber = restaurantInfo.whatsapp.replace(/\s/g, '');
  const formattedWhatsappNumber = whatsappNumber.startsWith('0') 
    ? '41' + whatsappNumber.substring(1) 
    : whatsappNumber;

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.15 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const formatWhatsAppMessage = () => {
    const messages = {
      it: {
        greeting: "🍽️ *NUOVA PRENOTAZIONE*",
        name: "👤 Nome",
        email: "📧 Email",
        phone: "📞 Telefono",
        date: "📅 Data",
        guests: "👥 Persone",
        notes: "📝 Note"
      },
      de: {
        greeting: "🍽️ *NEUE RESERVIERUNG*",
        name: "👤 Name",
        email: "📧 E-Mail",
        phone: "📞 Telefon",
        date: "📅 Datum",
        guests: "👥 Personen",
        notes: "📝 Anmerkungen"
      },
      en: {
        greeting: "🍽️ *NEW RESERVATION*",
        name: "👤 Name",
        email: "📧 Email",
        phone: "📞 Phone",
        date: "📅 Date",
        guests: "👥 Guests",
        notes: "📝 Notes"
      }
    };

    const m = messages[language];
    
    let message = `${m.greeting}\n\n`;
    message += `${m.name}: ${formData.name}\n`;
    if (formData.email) message += `${m.email}: ${formData.email}\n`;
    if (formData.phone) message += `${m.phone}: ${formData.phone}\n`;
    if (formData.date) {
      const dateFormatted = new Date(formData.date).toLocaleDateString(
        language === 'it' ? 'it-IT' : language === 'de' ? 'de-DE' : 'en-US',
        { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }
      );
      message += `${m.date}: ${dateFormatted}\n`;
    }
    message += `${m.guests}: ${formData.guests}\n`;
    if (formData.message) message += `\n${m.notes}: ${formData.message}`;
    
    return message;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const message = formatWhatsAppMessage();
    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/${formattedWhatsappNumber}?text=${encodedMessage}`, '_blank');
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <section
      id="contatti"
      ref={sectionRef}
      className="py-28 bg-[#3d2617] relative overflow-hidden"
    >
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#c9a227]/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#c75b39]/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Section header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <span className="inline-block text-[#c9a227] font-semibold tracking-wider uppercase text-sm mb-4">{t.contact.subtitle}</span>
          <h2 className="font-serif text-4xl md:text-6xl font-bold text-white mb-6">
            {t.contact.title}
          </h2>
          <div className="w-32 h-1 bg-gradient-to-r from-[#c9a227] via-[#e8d48b] to-[#c9a227] mx-auto rounded-full"></div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact info */}
          <div className={`transition-all duration-1000 delay-200 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 sm:p-8 border border-white/10 h-full overflow-hidden">
              <h3 className="font-serif text-xl sm:text-2xl font-bold text-white mb-6 sm:mb-8">{t.contact.info}</h3>
              
              <div className="space-y-5 sm:space-y-6">
                <a
                  href={`tel:${restaurantInfo.phone}`}
                  className="flex items-start gap-3 sm:gap-4 group hover:translate-x-2 transition-transform"
                >
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-gradient-to-br from-[#c9a227] to-[#8b7119] flex items-center justify-center flex-shrink-0 shadow-lg group-hover:scale-110 transition-transform">
                    <Phone className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs sm:text-sm text-white/60 mb-1">{t.contact.phone}</p>
                    <p className="font-bold text-lg sm:text-xl text-white group-hover:text-[#c9a227] transition-colors">{restaurantInfo.phone}</p>
                  </div>
                </a>

                <a
                  href={`mailto:${restaurantInfo.email}`}
                  className="flex items-start gap-3 sm:gap-4 group hover:translate-x-2 transition-transform"
                >
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-gradient-to-br from-[#c9a227] to-[#8b7119] flex items-center justify-center flex-shrink-0 shadow-lg group-hover:scale-110 transition-transform">
                    <Mail className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs sm:text-sm text-white/60 mb-1">{t.contact.email}</p>
                    <p className="font-bold text-sm sm:text-lg text-white group-hover:text-[#c9a227] transition-colors break-all">{restaurantInfo.email}</p>
                  </div>
                </a>

                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-gradient-to-br from-[#c9a227] to-[#8b7119] flex items-center justify-center flex-shrink-0 shadow-lg">
                    <MapPin className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs sm:text-sm text-white/60 mb-1">{t.contact.address}</p>
                    <p className="font-bold text-sm sm:text-lg text-white break-words">{restaurantInfo.address}</p>
                  </div>
                </div>

                <a
                  href={restaurantInfo.facebookUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-3 sm:gap-4 group hover:translate-x-2 transition-transform"
                >
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-gradient-to-br from-[#1877F2] to-[#0a5dc2] flex items-center justify-center flex-shrink-0 shadow-lg group-hover:scale-110 transition-transform">
                    <Facebook className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs sm:text-sm text-white/60 mb-1">{t.contact.facebook}</p>
                    <p className="font-bold text-sm sm:text-lg text-white group-hover:text-[#c9a227] transition-colors break-words">@ristorante.mezzaluna.riazzino</p>
                  </div>
                </a>
              </div>

              {/* Map */}
              <div className="mt-8 rounded-xl overflow-hidden h-52 bg-white/5 border border-white/10">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2770.123456789!2d8.83!3d46.25!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDbCsDE1JzAwLjAiTiA4wrA0OScwMC4wIkU!5e0!3m2!1sen!2sch!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Mezzaluna Location"
                ></iframe>
              </div>
            </div>
          </div>

          {/* Contact form */}
          <div className={`transition-all duration-1000 delay-400 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
            <div className="bg-white rounded-2xl p-8 shadow-2xl">
              <h3 className="font-serif text-2xl font-bold text-[#3d2617] mb-6">{t.contact.form.title}</h3>

              <form onSubmit={handleSubmit} className="space-y-5" data-testid="booking-form">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-[#3d2617] mb-2">{t.contact.form.name} *</label>
                    <Input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      data-testid="booking-name-input"
                      className="border-[#e8dcc8] focus:border-[#c9a227] focus:ring-[#c9a227] h-12"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#3d2617] mb-2">{t.contact.form.email}</label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      data-testid="booking-email-input"
                      className="border-[#e8dcc8] focus:border-[#c9a227] focus:ring-[#c9a227] h-12"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-[#3d2617] mb-2">{t.contact.form.phone}</label>
                    <Input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      data-testid="booking-phone-input"
                      className="border-[#e8dcc8] focus:border-[#c9a227] focus:ring-[#c9a227] h-12"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#3d2617] mb-2">{t.contact.form.guests}</label>
                    <select
                      name="guests"
                      value={formData.guests}
                      onChange={handleChange}
                      data-testid="booking-guests-select"
                      className="w-full h-12 px-3 py-2 rounded-md border border-[#e8dcc8] text-[#3d2617] focus:border-[#c9a227] focus:ring-1 focus:ring-[#c9a227] focus:outline-none"
                    >
                      {[1, 2, 3, 4, 5, 6, 7, 8, '9+'].map(n => (
                        <option key={n} value={n}>{n}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#3d2617] mb-2">{t.contact.form.date}</label>
                  <Input
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={handleChange}
                    data-testid="booking-date-input"
                    className="border-[#e8dcc8] focus:border-[#c9a227] focus:ring-[#c9a227] h-12"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#3d2617] mb-2">{t.contact.form.message}</label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    data-testid="booking-message-textarea"
                    className="border-[#e8dcc8] focus:border-[#c9a227] focus:ring-[#c9a227]"
                    placeholder={t.contact.form.messagePlaceholder}
                  />
                </div>

                <Button
                  type="submit"
                  data-testid="booking-submit-whatsapp"
                  className="w-full bg-[#25D366] hover:bg-[#1da851] text-white font-bold py-6 rounded-xl transition-all duration-300 shadow-xl hover:shadow-2xl text-lg"
                >
                  <svg viewBox="0 0 24 24" className="w-6 h-6 mr-2" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  {t.contact.form.submitWhatsApp || t.contact.form.submit}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
